import { useMemo } from "react";

import { useSelector } from "react-redux";

import DurationService from "app/services/DurationService";
import { isLinesOfBusinessSegment, parseLinesOfBusinessSegmentId } from "app/services/segments/LinesOfBusinessUtils";
import type { ISegmentsModule } from "app/services/segments/segmentsApiService";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import { getAverage } from "app/UtilitiesAndConstants/UtilityFunctions/statistics";
import { EChartDisplayType } from "pro-features/components/Chart/src/configs/column-chart/columnChartConfig";
import type { IColumnChartDataItem } from "pro-features/components/Chart/src/types";
import { resolveMaxYaxisValue } from "pro-features/components/Chart/src/utils";

import { MarketSharesPeriodOverPeriod } from "./MarketSharesPeriodOverPeriod";
import { MarketSharesSinglePeriod } from "./MarketSharesSinglePeriod";
import type { ChartData, EnrichedData, IChart, SwitchGraphDomainData } from "./types";
import { CHART_DISPLAY_TYPE_MD, getFadedColumnColor } from "./utils/utils";

interface IAverageChart {
    chartData: ChartData;
    compareChartData: ChartData;
    enrichedData: EnrichedData;
    rawDomainData: SwitchGraphDomainData[];
    chartDisplayType: EChartDisplayType;
}

export const AverageChart = ({
    chartData,
    compareChartData,
    enrichedData,
    rawDomainData,
    chartDisplayType,
}: IAverageChart) => {
    const segmentsModule: ISegmentsModule = useSelector((state) => {
        const { segmentsModule } = state;
        return segmentsModule;
    });
    const getUserAndAccountSegmentById = useMemo(
        () => SegmentsUtils.getUserAndAccountSegmentByIdHof(segmentsModule),
        [],
    );
    const { formatter } = CHART_DISPLAY_TYPE_MD[chartDisplayType];

    const mapperFunction = (colorGetter: (it: IChart) => string): ((dataItem: IChart) => IColumnChartDataItem) => {
        return (dataItem) => {
            const { id } = dataItem;
            const segment = getUserAndAccountSegmentById(id);
            const y = dataItem.average ?? getAverage(dataItem.data);
            const regionalDomains = enrichedData[id]?.regionalDomains;

            // Handle LOB segments when no regular segment is found
            let lobDomainName = null;
            let lobSegmentName = null;
            let isLobSegment = false;
            if (!segment && isLinesOfBusinessSegment(id)) {
                isLobSegment = true;
                const enrichedLobData = enrichedData[id];

                if (enrichedLobData?.name) {
                    lobSegmentName = enrichedLobData.name;
                    lobDomainName = enrichedLobData.domain;
                } else {
                    // Fallback to parsing the segment ID
                    const lobSegmentData = parseLinesOfBusinessSegmentId(id);
                    if (lobSegmentData) {
                        lobDomainName = lobSegmentData.domain;
                        lobSegmentName = lobSegmentData.businessLineName.split("_").join(" ");
                    }
                }
            }

            const sharedAttributes = {
                id,
                y,
                displayText: dataItem.name,
                favicon: enrichedData[id]?.favicon,
                color: colorGetter(dataItem),
            };

            if (segment) {
                const { segmentName: subDisplayText, domain } = segment;
                return {
                    ...sharedAttributes,
                    subDisplayText,
                    domain,
                    isSegment: true,
                    displayText: domain,
                };
            } else if (isLobSegment && lobDomainName) {
                return {
                    ...sharedAttributes,
                    subDisplayText: lobSegmentName,
                    domain: lobDomainName,
                    isSegment: true,
                    displayText: lobDomainName,
                };
            } else if (regionalDomains?.length > 1) {
                return {
                    ...sharedAttributes,
                    regionalDomainsCount: regionalDomains.length,
                    domain: id,
                };
            }
            return { ...sharedAttributes, domain: id };
        };
    };

    const columnChartData = chartData.map(mapperFunction((it) => it.color));

    const compareColumnChartData = compareChartData.map(mapperFunction((it) => getFadedColumnColor(it.color)));

    const maxYaxisValue = useMemo(() => {
        if (chartDisplayType === EChartDisplayType.PERCENTAGE) {
            return null;
        }
        return resolveMaxYaxisValue(columnChartData);
    }, [chartDisplayType, columnChartData]);

    const isPeriodOverPeriod = DurationService.isPeriodOverPeriod();

    return isPeriodOverPeriod ? (
        <MarketSharesPeriodOverPeriod
            chartData={columnChartData}
            compareChartData={compareColumnChartData}
            chartDisplayType={chartDisplayType}
            labelFormatter={formatter}
        />
    ) : (
        <MarketSharesSinglePeriod
            chartData={columnChartData}
            chartDisplayType={chartDisplayType}
            labelFormatter={formatter}
            maxYaxisValue={maxYaxisValue}
            domains={rawDomainData}
        />
    );
};
